import { nsSimCard } from '@/app/i18n/namespaces';
import SIM_CARDS from '../../api/apiUrls';
import Entity from '@/common-utils/components/renders/entity/Entity';
import SimImportSelectProvider from './components/SimImportSelectProvider';
import CUSTOM_PAY_SIM_OFFERS_PROVIDERS from '../../enums/CustomPaySimOffersProviders';

const getCsvContentErrors = (fileDetails) => {
    const iccidsFromOtherProviders = fileDetails?.iccidsFromOtherProviders || [];
    const iccidsFromUnknwownClients = fileDetails?.iccidsFromUnknwownClients || [];
    const iccidsNotFound = fileDetails?.iccidsNotFound || [];
    const iccidsWithWrongOperations = fileDetails?.iccidsWithWrongOperations || [];
    return {
        iccidsFromOtherProviders,
        iccidsFromUnknwownClients,
        iccidsNotFound,
        iccidsWithWrongOperations
    };
};

const isCsvContentOnError = (fileDetails) => {
    const { iccidsFromOtherProviders, iccidsFromUnknwownClients, iccidsNotFound, iccidsWithWrongOperations } = getCsvContentErrors(fileDetails);
    return iccidsFromOtherProviders.length > 0
        || iccidsFromUnknwownClients.length > 0
        || iccidsNotFound.length > 0
        || iccidsWithWrongOperations.length > 0;
};

const SimImportProps = {
    apiUrl: SIM_CARDS.FILE_ACTION.url,
    namespace: nsSimCard,
    fileExampleProperties: {
        name: 'sim_operations_example.csv',
        path: '/static/resource/files/sim_operations_example.csv'
    },
    uploadProperties: {
        fileExtAccepted: ['.csv'],
        typeOfUpload: 'text',
        invalidFileEntity: 'modal.import.error.invalid_file',
        maxSize: 10485760
    },
    countLines: (fileData) => {
        const counters = {
            activations: 0,
            terminations: 0
        };
        const lines = fileData.split('\n');
        for (const line of lines) {
            const [operation] = line.split(';');
            if (operation === 'ACTIVATION') {
                ++counters.activations;
            } else if (operation === 'DELETION') {
                ++counters.terminations;
            }
        }
        return { numberOfLines: counters, file: fileData };
    },
    explanations: ({ stepData }) => {
        return (<>
            <Entity entity='modal.import.explanations.header' namespace={nsSimCard} />
            <ul className='list-disc list-outside [&_ul]:list-[revert] ml-8'>
                <li><Entity entity='modal.import.explanations.operation' namespace={nsSimCard} /></li>
                <li><Entity entity='modal.import.explanations.iccid' namespace={nsSimCard} /></li>
                <li>
                    <Entity entity='modal.import.explanations.contract' namespace={nsSimCard} />
                    <ul className='list-outside ml-8'>
                        {
                            CUSTOM_PAY_SIM_OFFERS_PROVIDERS[stepData[0]]?.offersEntities.map((offer) => {
                                return <li><Entity entity={offer} namespace={nsSimCard} /></li>;
                            })
                        }
                    </ul>
                </li >
                <li><Entity entity='modal.import.explanations.client' namespace={nsSimCard} /></li>
            </ul >
        </>);
    },
    confirmation: (numberOfLines) => {
        return <Entity
            entity='modal.import.confirmation'
            namespace={nsSimCard}
            data={{ activations: numberOfLines.activations, terminations: numberOfLines.terminations }}
        />;
    },
    extraSteps: [
        {
            title: 'modal.import.extra_steps.title.select_provider',
            namespace: nsSimCard,
            modalClassName: 'max-w-lg',
            disabledNext: (provider) => {
                return !provider;
            },
            render: ({ step, stepData, setStepData }) => {
                return <SimImportSelectProvider
                    step={step}
                    stepData={stepData}
                    setStepData={setStepData}
                />;
            }
        }
    ],
    extraData: (stepData) => {
        return {
            provider: stepData[0]
        };
    },
    isFileOnError: (fileDetails) => {
        return isCsvContentOnError(fileDetails);
    },
    renderPostImportErrors: (fileDetails, error) => {
        const isCsvUploadOnError = error?.response?.data;
        if (isCsvUploadOnError) {
            const errorCode = error.response.data.errorCode;
            if (errorCode === 'BAD_FILE_FORMAT_EXCEPTION') {
                return (
                    <div>
                        <Entity entity='modal.import.error.BAD_FILE_FORMAT_EXCEPTION' namespace={nsSimCard} />
                        <br />
                        <br />
                        <Entity entity='modal.import.error.get_example_file' namespace={nsSimCard} />
                    </div>
                );
            }
            return (
                <Entity entity='modal.import.error.UNEXPECTED_ERROR' namespace={nsSimCard} />
            );
        }


        // operation: ACTIVATION or DELETION
        // iccid: SIM card ICCID
        // contrat: SIM plan linked to a Sim Provider
        // client: Targeted organization
        if (isCsvContentOnError(fileDetails)) {
            const { iccidsFromOtherProviders,
                iccidsFromUnknwownClients,
                iccidsNotFound,
                iccidsWithWrongOperations
            } = getCsvContentErrors(fileDetails);

            const errorsList = [
                iccidsFromOtherProviders.length > 0 && {
                    entity: 'modal.import.error.csv_content.iccids.from_other_providers',
                    value: iccidsFromOtherProviders.join(', ')
                },
                iccidsFromUnknwownClients.length > 0 && {
                    entity: 'modal.import.error.csv_content.iccids.from_unknwown_clients',
                    value: iccidsFromUnknwownClients.join(', ')
                },
                iccidsNotFound.length > 0 && {
                    entity: 'modal.import.error.csv_content.iccids.not_found',
                    value: iccidsNotFound.join(', ')
                },
                iccidsWithWrongOperations.length > 0 && {
                    entity: 'modal.import.error.csv_content.iccids.with_wrong_operations',
                    value: iccidsWithWrongOperations.join(', ')
                }
            ].filter(Boolean); // Filters falsy values (undefined, null, false, 0, etc.)

            return (
                <div className='flex flex-col gap-2'>
                    <Entity entity='modal.import.error.csv_content.subtitle' namespace={nsSimCard} />
                    <Entity entity='modal.import.error.csv_content.header' namespace={nsSimCard} />
                    <ul className='list-disc list-outside [&_ul]:list-[revert] ml-8'>
                        {
                            errorsList.map((errorItem) => {
                                return <li><Entity className='font-bold' entity={errorItem.entity} namespace={nsSimCard} />{errorItem.value}</li>;
                            })
                        }
                    </ul>
                </div>
            );
        }
    }
};

export default SimImportProps;